import React from "react";

// ─────────────────────────────────────────────────────────────────
// STYLE SYSTEM  (matches existing pages exactly)
// ─────────────────────────────────────────────────────────────────
const S = {
  wrapper:      { marginTop:'10px', marginLeft:'0px', border:'3px', padding:'40px', borderStyle:'solid', borderColor:'grey', fontFamily:"'Georgia', serif", color:'#1a1a2e', lineHeight:'1.7' },
  h1:           { fontSize:'2rem', fontWeight:'700', borderBottom:'3px solid #2E75B6', paddingBottom:'8px', marginBottom:'24px', color:'#1F3864' },
  h2:           { fontSize:'1.3rem', fontWeight:'700', color:'#2E75B6', marginTop:'36px', marginBottom:'12px', borderLeft:'4px solid #2E75B6', paddingLeft:'10px' },
  p:            { marginBottom:'12px', fontSize:'0.97rem' },
  infoBox:      { background:'#EBF3FB', border:'2px solid #2E75B6', borderRadius:'6px', padding:'16px 20px', margin:'16px 0' },
  infoBoxTitle: { fontWeight:'700', fontFamily:"'Courier New', monospace", fontSize:'0.88rem', color:'#1F3864', marginBottom:'8px' },
  infoBoxItem:  { fontSize:'0.9rem', margin:'4px 0', paddingLeft:'12px', borderLeft:'2px solid #2E75B6' },
  table:        { width:'100%', borderCollapse:'collapse', marginBottom:'20px', fontSize:'0.92rem' },
  th:           { background:'#2E75B6', color:'#fff', padding:'10px 14px', textAlign:'left', fontWeight:'700', fontSize:'0.9rem' },
  tdE:          { padding:'10px 14px', borderBottom:'1px solid #ccd9ea', verticalAlign:'top', background:'#f5f9ff' },
  tdO:          { padding:'10px 14px', borderBottom:'1px solid #ccd9ea', verticalAlign:'top', background:'#fff' },
  tdL:          { fontWeight:'600', color:'#1F3864', whiteSpace:'nowrap', width:'220px' },
  code:         { fontFamily:"'Courier New', monospace", fontSize:'0.88rem', whiteSpace:'pre-wrap' },
  dWrap:        { margin:'24px 0', background:'#f8fbff', border:'1px solid #ccd9ea', borderRadius:'8px', padding:'20px' },
  dTitle:       { fontWeight:'700', fontSize:'0.95rem', color:'#1F3864', marginBottom:'12px', textAlign:'center', letterSpacing:'0.02em' },
  dCaption:     { fontSize:'0.84rem', color:'#555', textAlign:'center', marginTop:'10px', fontStyle:'italic' },
};

function InfoBox({ title, items }) {
  return (
    <div style={S.infoBox}>
      <div style={S.infoBoxTitle}>{title}</div>
      {items.map((it, i) => <div key={i} style={S.infoBoxItem}>{it}</div>)}
    </div>
  );
}

function Table2({ rows }) {
  return (
    <table style={S.table}>
      <thead><tr>
        <th style={{ ...S.th, width:'220px' }}>Concept</th>
        <th style={S.th}>Definition / Notes</th>
      </tr></thead>
      <tbody>
        {rows.map(([label, content], i) => (
          <tr key={i}>
            <td style={{ ...(i%2===0?S.tdE:S.tdO), ...S.tdL }}>{label}</td>
            <td style={{ ...(i%2===0?S.tdE:S.tdO), ...S.code }}>{content}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

// Bracket helper
function Bk({ x, y, h, side, color='#1F3864', w=10 }) {
  if (side==='left')  return <path d={`M${x+w},${y} L${x},${y} L${x},${y+h} L${x+w},${y+h}`} fill="none" stroke={color} strokeWidth="2"/>;
  if (side==='right') return <path d={`M${x-w},${y} L${x},${y} L${x},${y+h} L${x-w},${y+h}`} fill="none" stroke={color} strokeWidth="2"/>;
  return null;
}

// Arrow marker
function Arr({ id, color }) {
  return (
    <marker id={id} markerWidth="7" markerHeight="7" refX="6" refY="3.5" orient="auto">
      <polygon points="0 0,7 3.5,0 7" fill={color}/>
    </marker>
  );
}

// Compact matrix cell renderer
function Mat({ data, x0, y0, cW=52, cH=38, colors=null, color='#1F3864', fSize=13 }) {
  const R = data.length, C = data[0].length;
  return (
    <g>
      <Bk x={x0-5}     y={y0-4} h={R*cH+8} side="left"  color={color}/>
      <Bk x={x0+C*cW+5} y={y0-4} h={R*cH+8} side="right" color={color}/>
      {data.map((row, r) => row.map((val, c) => {
        const bg = colors?.[r]?.[c] ?? null;
        return (
          <g key={`${r}${c}`}>
            {bg && <rect x={x0+c*cW+2} y={y0+r*cH+2} width={cW-4} height={cH-4} rx={3} fill={bg} opacity="0.7"/>}
            <text x={x0+c*cW+cW/2} y={y0+r*cH+cH/2+5}
              textAnchor="middle" fontSize={fSize} fill="#1F3864"
              fontFamily="'Courier New',monospace" fontWeight={bg?'700':'400'}>{val}</text>
          </g>
        );
      }))}
    </g>
  );
}

// ─────────────────────────────────────────────────────────────────
// DIAGRAM 1 — LU Decomposition: Doolittle vs Crout + pivoting
// Full structural breakdown + two-solve strategy with numbers
// ─────────────────────────────────────────────────────────────────
function DiagramLU() {
  const W = 880, H = 370;

  // A = [[2,1,1],[4,3,3],[8,7,9]]
  // Doolittle: L has 1s on diagonal, U has pivots on diagonal
  // L = [[1,0,0],[2,1,0],[4,3,1]]  U = [[2,1,1],[0,1,1],[0,0,2]]
  // P = I (no pivoting needed for this example)
  const A  = [['2','1','1'],['4','3','3'],['8','7','9']];
  const L  = [['1','0','0'],['2','1','0'],['4','3','1']];
  const U  = [['2','1','1'],['0','1','1'],['0','0','2']];

  const cL = [
    ['#d0e8ff', null,        null],
    ['#ffd0d0','#d0e8ff',    null],
    ['#ffd0d0','#ffd0d0','#d0e8ff'],
  ];
  const cU = [
    ['#d4f0d4','#d4f0d4','#d4f0d4'],
    [null,      '#d4f0d4','#d4f0d4'],
    [null,       null,    '#d4f0d4'],
  ];

  return (
    <div style={S.dWrap}>
      <div style={S.dTitle}>LU Decomposition — Structure, Variants, and the Two-Solve Strategy</div>
      <svg viewBox={`0 0 ${W} ${H}`} style={{ width:'100%', display:'block' }}>

        {/* ── TOP: A = L × U with colour-coded triangles ── */}
        {/* A label */}
        <text x={62}  y={28} textAnchor="middle" fontSize="15" fill="#1F3864" fontWeight="700">A</text>
        <Mat data={A} x0={16} y0={38} cW={52} cH={42}/>
        <text x={188} y={80} fontSize="26" fill="#aaa">=</text>

        {/* L label + matrix */}
        <text x={278} y={18} textAnchor="middle" fontSize="13" fill="#c0392b" fontWeight="700">L  (lower, unit diagonal)</text>
        <Mat data={L} x0={212} y0={38} cW={52} cH={42} colors={cL} color="#c0392b"/>
        <text x={384} y={80} fontSize="26" fill="#aaa">×</text>

        {/* U label + matrix */}
        <text x={484} y={18} textAnchor="middle" fontSize="13" fill="#27ae60" fontWeight="700">U  (upper, pivots on diagonal)</text>
        <Mat data={U} x0={408} y0={38} cW={52} cH={42} colors={cU} color="#27ae60"/>

        {/* Legend */}
        <rect x={584} y={26} width={288} height={120} rx={5} fill="#fff" stroke="#ccd9ea" strokeWidth="1"/>
        <text x={728} y={44} textAnchor="middle" fontSize="12" fill="#1F3864" fontWeight="700">Doolittle vs Crout variants</text>
        <rect x={594} y={52} width={12} height={12} rx={2} fill="#d0e8ff" stroke="#2E75B6" strokeWidth="1"/>
        <text x={612} y={63} fontSize="11.5" fill="#2E75B6" fontWeight="700">Doolittle:</text>
        <text x={612} y={77} fontSize="11"   fill="#555">L has 1s on diagonal (shown here)</text>
        <text x={612} y={91} fontSize="11"   fill="#555">U carries the pivot values</text>
        <rect x={594} y={100} width={12} height={12} rx={2} fill="#ffd0d0" stroke="#c0392b" strokeWidth="1"/>
        <text x={612} y={111} fontSize="11.5" fill="#c0392b" fontWeight="700">Crout:</text>
        <text x={612} y={125} fontSize="11"   fill="#555">L carries the pivot values</text>
        <text x={612} y={139} fontSize="11"   fill="#555">U has 1s on diagonal (less common)</text>

        {/* Divider */}
        <line x1={10} y1={162} x2={870} y2={162} stroke="#e0e8f0" strokeWidth="1.5"/>

        {/* ── BOTTOM: Two-solve pipeline with actual numbers ── */}
        <text x={440} y={182} textAnchor="middle" fontSize="13" fill="#1F3864" fontWeight="700">
          Solving Ax=b via LU — Factor Once, Solve Many Right-Hand Sides Cheaply
        </text>

        {/* b vector */}
        <text x={52} y={206} textAnchor="middle" fontSize="12" fill="#888">b</text>
        <Mat data={[['1'],['3'],['5']]} x0={28} y0={214} cW={44} cH={38} color="#888"/>

        {/* Step 1: Ly = b */}
        <rect x={98} y={198} width={226} height={130} rx={5} fill="#fff" stroke="#c0392b" strokeWidth="1.4"/>
        <text x={211} y={215} textAnchor="middle" fontSize="12" fill="#c0392b" fontWeight="700">Step 1 — Forward Sub: Ly = b</text>
        <text x={108} y={233} fontSize="11.5" fill="#1F3864" fontFamily="'Courier New',monospace">y₁ = 1</text>
        <text x={108} y={251} fontSize="11.5" fill="#1F3864" fontFamily="'Courier New',monospace">y₂ = 3 − 2(1)  = 1</text>
        <text x={108} y={269} fontSize="11.5" fill="#1F3864" fontFamily="'Courier New',monospace">y₃ = 5 − 4(1) − 3(1) = −2</text>
        <rect x={108} y={282} width={206} height={38} rx={3} fill="#fff0f0" stroke="#c0392b" strokeWidth="1"/>
        <text x={211} y={298} textAnchor="middle" fontSize="12" fill="#c0392b" fontFamily="'Courier New',monospace" fontWeight="700">y = [1, 1, −2]</text>
        <text x={211} y={312} textAnchor="middle" fontSize="10.5" fill="#888">O(n²) — sweep top to bottom</text>

        {/* Arrow */}
        <text x={335} y={268} fontSize="20" fill="#aaa">→</text>

        {/* Step 2: Ux = y */}
        <rect x={356} y={198} width={226} height={130} rx={5} fill="#fff" stroke="#27ae60" strokeWidth="1.4"/>
        <text x={469} y={215} textAnchor="middle" fontSize="12" fill="#27ae60" fontWeight="700">Step 2 — Back Sub: Ux = y</text>
        <text x={366} y={233} fontSize="11.5" fill="#1F3864" fontFamily="'Courier New',monospace">x₃ = −2 / 2  = −1</text>
        <text x={366} y={251} fontSize="11.5" fill="#1F3864" fontFamily="'Courier New',monospace">x₂ = 1 − (1)(−1) = 2</text>
        <text x={366} y={269} fontSize="11.5" fill="#1F3864" fontFamily="'Courier New',monospace">x₁ = (1−1(2)−1(−1))/2 = 0</text>
        <rect x={366} y={282} width={206} height={38} rx={3} fill="#f0fff4" stroke="#27ae60" strokeWidth="1"/>
        <text x={469} y={298} textAnchor="middle" fontSize="12" fill="#27ae60" fontFamily="'Courier New',monospace" fontWeight="700">x = [0, 2, −1]</text>
        <text x={469} y={312} textAnchor="middle" fontSize="10.5" fill="#888">O(n²) — sweep bottom to top</text>

        {/* Key insight box */}
        <rect x={596} y={198} width={276} height={130} rx={5} fill="#EBF3FB" stroke="#2E75B6" strokeWidth="1.4"/>
        <text x={734} y={215} textAnchor="middle" fontSize="12" fill="#1F3864" fontWeight="700">Why LU beats repeated elimination</text>
        <text x={606} y={234} fontSize="11.5" fill="#555">Factor A once:     O(n³)</text>
        <text x={606} y={252} fontSize="11.5" fill="#555">Each new b costs:  O(n²)</text>
        <line x1={606} y1={262} x2={862} y2={262} stroke="#ccd9ea" strokeWidth="1"/>
        <text x={606} y={280} fontSize="11.5" fill="#1F3864" fontWeight="700">Partial pivoting: PA = LU</text>
        <text x={606} y={296} fontSize="11" fill="#555">Swap rows to put largest element</text>
        <text x={606} y={310} fontSize="11" fill="#555">in pivot position → stable numerics</text>
        <text x={606} y={324} fontSize="10.5" fill="#888">All solvers (MATLAB \, numpy.linalg)</text>
        <text x={606} y={338} fontSize="10.5" fill="#888">use LU with partial pivoting internally</text>
      </svg>
      <div style={S.dCaption}>
        LU factorises A into a lower-triangular L (recording the elimination multipliers) and upper-triangular U (the row-echelon form). Solving Ax=b then becomes two O(n²) triangular sweeps instead of one O(n³) elimination per right-hand side — critical when solving thousands of load cases on the same FEA stiffness matrix.
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────
// DIAGRAM 2 — Cholesky: A = LLᵀ for symmetric positive definite
// Show structure, formula, and speed comparison vs LU
// ─────────────────────────────────────────────────────────────────
function DiagramCholesky() {
  const W = 880, H = 320;

  // Symmetric PD example: A = [[4,2,2],[2,3,2],[2,2,3]]
  // Cholesky: L = [[2,0,0],[1,√2,0],[1,1/√2,√(3/2)]]
  // Let's use exact fractions for display clarity
  // L = [[2,0,0],[1,√2,0],[1,1/√2,√(4/3)]] — approximate display
  const A  = [['4','2','2'],['2','3','2'],['2','2','3']];
  const Lc = [['2','0','0'],['1','√2','0'],['1','½√2','⅔√6']];
  const Lt = [['2','1','1'],['0','√2','½√2'],['0','0','⅔√6']];

  const cL = [
    ['#d0e8ff', null, null],
    ['#ffd0d0','#d0e8ff', null],
    ['#ffd0d0','#ffd0d0','#d0e8ff'],
  ];
  const cLt = [
    ['#d0e8ff','#ffd0d0','#ffd0d0'],
    [null,'#d0e8ff','#ffd0d0'],
    [null, null,'#d0e8ff'],
  ];

  return (
    <div style={S.dWrap}>
      <div style={S.dTitle}>Cholesky Decomposition — A = LLᵀ for Symmetric Positive Definite Matrices</div>
      <svg viewBox={`0 0 ${W} ${H}`} style={{ width:'100%', display:'block' }}>

        {/* ── LEFT: A = L Lᵀ layout ── */}
        <text x={62} y={28} textAnchor="middle" fontSize="15" fill="#1F3864" fontWeight="700">A  (sym. PD)</text>
        <Mat data={A} x0={10} y0={42} cW={52} cH={40} color="#9b59b6"/>
        <text x={184} y={84} fontSize="26" fill="#aaa">=</text>

        <text x={280} y={18} textAnchor="middle" fontSize="13" fill="#2E75B6" fontWeight="700">L  (lower triangular)</text>
        <Mat data={Lc} x0={210} y0={42} cW={56} cH={40} colors={cL} color="#2E75B6"/>
        <text x={394} y={84} fontSize="24" fill="#aaa">·</text>

        <text x={496} y={18} textAnchor="middle" fontSize="13" fill="#c0392b" fontWeight="700">Lᵀ  (upper = transpose of L)</text>
        <Mat data={Lt} x0={415} y0={42} cW={56} cH={40} colors={cLt} color="#c0392b"/>

        {/* Mirror arrow annotation */}
        <text x={560} y={176} textAnchor="middle" fontSize="11" fill="#9b59b6" fontStyle="italic">Lᵀ is just L flipped — no new computation</text>

        {/* ── MIDDLE: formula box ── */}
        <rect x={620} y={8} width={252} height={168} rx={5} fill="#fff" stroke="#9b59b6" strokeWidth="1.5"/>
        <text x={746} y={26} textAnchor="middle" fontSize="12.5" fill="#9b59b6" fontWeight="700">Cholesky Algorithm</text>
        <text x={630} y={46} fontSize="11.5" fill="#1F3864" fontWeight="700">For each column j:</text>
        <text x={630} y={63} fontSize="11" fill="#1F3864" fontFamily="'Courier New',monospace">Lⱼⱼ = √(Aⱼⱼ − Σₖ Lⱼₖ²)</text>
        <text x={630} y={82} fontSize="11" fill="#1F3864" fontWeight="700">For each row i &gt; j:</text>
        <text x={630} y={99} fontSize="11" fill="#1F3864" fontFamily="'Courier New',monospace">Lᵢⱼ = (Aᵢⱼ − Σₖ LᵢₖLⱼₖ)/Lⱼⱼ</text>
        <line x1={630} y1={110} x2={862} y2={110} stroke="#eee" strokeWidth="1"/>
        <text x={630} y={126} fontSize="11" fill="#27ae60" fontWeight="700">Requires A symmetric and PD</text>
        <text x={630} y={142} fontSize="11" fill="#555">If sqrt of negative appears: A is</text>
        <text x={630} y={157} fontSize="11" fill="#555">not PD — decomposition fails safely</text>

        {/* ── BOTTOM: Comparison bar ── */}
        <line x1={10} y1={188} x2={870} y2={188} stroke="#e0e8f0" strokeWidth="1.5"/>
        <text x={440} y={207} textAnchor="middle" fontSize="13" fill="#1F3864" fontWeight="700">
          Cholesky vs LU — Why Use Cholesky When You Can?
        </text>

        {[
          { label:'Operation count', lu:'~(2/3)n³  flops', ch:'~(1/3)n³  flops', win:'Cholesky is 2× faster' },
          { label:'Memory',          lu:'Store L and U separately', ch:'Only store L  (Lᵀ is free)', win:'Cholesky uses half storage' },
          { label:'Numerical stability', lu:'Requires pivoting for stability', ch:'Always stable for PD (no pivoting)', win:'Cholesky is unconditionally stable' },
          { label:'Applicability',   lu:'Any square matrix', ch:'Symmetric positive definite only', win:'LU is more general' },
        ].map(({ label, lu, ch, win }, i) => (
          <g key={i}>
            <rect x={10}  y={216+i*24} width={145} height={20} rx={2} fill={i%2===0?'#f5f9ff':'#fff'}/>
            <rect x={158} y={216+i*24} width={230} height={20} rx={2} fill={i%2===0?'#f5f9ff':'#fff'}/>
            <rect x={391} y={216+i*24} width={240} height={20} rx={2} fill={i%2===0?'#f5f9ff':'#fff'}/>
            <rect x={634} y={216+i*24} width={236} height={20} rx={2} fill={i===3?'#fff7ee':'#f0fff4'}/>
            <text x={16}  y={230+i*24} fontSize="11" fill="#1F3864" fontWeight="700">{label}</text>
            <text x={164} y={230+i*24} fontSize="11" fill="#c0392b" fontFamily="'Courier New',monospace">{lu}</text>
            <text x={397} y={230+i*24} fontSize="11" fill="#2E75B6" fontFamily="'Courier New',monospace">{ch}</text>
            <text x={640} y={230+i*24} fontSize="11" fill={i===3?'#e07b00':'#27ae60'} fontWeight="700">{win}</text>
          </g>
        ))}

        {/* Column headers */}
        <text x={83}  y={212} textAnchor="middle" fontSize="11" fill="#888" fontWeight="700">Property</text>
        <text x={273} y={212} textAnchor="middle" fontSize="11" fill="#c0392b" fontWeight="700">LU</text>
        <text x={511} y={212} textAnchor="middle" fontSize="11" fill="#2E75B6" fontWeight="700">Cholesky</text>
        <text x={752} y={212} textAnchor="middle" fontSize="11" fill="#555" fontWeight="700">Winner</text>
        <line x1={10} y1={213} x2={870} y2={213} stroke="#ccd9ea" strokeWidth="1"/>

        <rect x={10} y={316} width={860} height={0} rx={3}/>
      </svg>
      <div style={S.dCaption}>
        Cholesky is the specialised, faster LU for symmetric positive definite matrices. Every FEA stiffness matrix satisfies this condition, so FEA solvers always use Cholesky (or its sparse variant) rather than general LU. The square-root test in the algorithm also acts as a numerical check that the matrix is truly positive definite.
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────
// DIAGRAM 3 — QR Decomposition: Gram-Schmidt geometry + least squares
// ─────────────────────────────────────────────────────────────────
function DiagramQR() {
  const W = 880, H = 350;
  const sc = 52;

  // Gram-Schmidt in 2D: a1=(3,1), a2=(2,2)
  // q1 = a1/|a1| = (3,1)/√10
  // proj = (a2·q1)q1 = (8/√10)*(3,1)/√10 = (24/10, 8/10) = (2.4, 0.8)
  // a2_perp = a2 - proj = (2-2.4, 2-0.8) = (-0.4, 1.2)
  // q2 = a2_perp / |a2_perp| = (-0.4,1.2)/√(0.16+1.44) = (-0.4,1.2)/√1.6
  const sq10 = Math.sqrt(10);
  const q1 = [3/sq10, 1/sq10];
  const a1 = [3, 1], a2 = [2, 2];
  const dot = a2[0]*q1[0] + a2[1]*q1[1]; // 8/√10
  const proj = [dot*q1[0], dot*q1[1]];   // (2.4, 0.8)
  const perp = [a2[0]-proj[0], a2[1]-proj[1]]; // (-0.4, 1.2)
  const plen = Math.sqrt(perp[0]**2 + perp[1]**2);
  const q2 = [perp[0]/plen, perp[1]/plen];

  const ox = 140, oy = 210;

  return (
    <div style={S.dWrap}>
      <div style={S.dTitle}>QR Decomposition — Orthonormal Columns via Gram-Schmidt, and Numerically Stable Least Squares</div>
      <svg viewBox={`0 0 ${W} ${H}`} style={{ width:'100%', display:'block' }}>
        <defs>
          <Arr id="qr_a1"   color="#2E75B6"/>
          <Arr id="qr_a2"   color="#c0392b"/>
          <Arr id="qr_q1"   color="#27ae60"/>
          <Arr id="qr_q2"   color="#9b59b6"/>
          <Arr id="qr_proj" color="#e07b00"/>
          <Arr id="qr_perp" color="#9b59b6"/>
        </defs>

        {/* ── LEFT: Gram-Schmidt geometry ── */}
        <rect x={10} y={8} width={420} height={332} rx={6} fill="#fff" stroke="#27ae60" strokeWidth="1.6"/>
        <text x={220} y={26} textAnchor="middle" fontSize="13" fill="#1F3864" fontWeight="700">
          Gram-Schmidt Process — Building Orthonormal Columns
        </text>
        <text x={220} y={42} textAnchor="middle" fontSize="11.5" fill="#555">
          Start with columns of A, produce orthonormal basis Q
        </text>

        {/* Grid */}
        {[-1,0,1,2,3].map(v => (
          <g key={v}>
            <line x1={18}  y1={oy+v*sc} x2={422} y2={oy+v*sc} stroke="#f0f0f0" strokeWidth="1"/>
            <line x1={ox+v*sc} y1={50}  x2={ox+v*sc} y2={330} stroke="#f0f0f0" strokeWidth="1"/>
          </g>
        ))}
        <line x1={18}  y1={oy} x2={422} y2={oy} stroke="#ccc" strokeWidth="1.3"/>
        <line x1={ox}  y1={50} x2={ox}  y2={330} stroke="#ccc" strokeWidth="1.3"/>

        {/* a1, a2 — original columns */}
        <line x1={ox} y1={oy} x2={ox+a1[0]*sc-3} y2={oy-a1[1]*sc}
          stroke="#2E75B6" strokeWidth="2.5" markerEnd="url(#qr_a1)"/>
        <text x={ox+a1[0]*sc+5} y={oy-a1[1]*sc+4} fontSize="13" fill="#2E75B6" fontWeight="700">a₁</text>

        <line x1={ox} y1={oy} x2={ox+a2[0]*sc-3} y2={oy-a2[1]*sc}
          stroke="#c0392b" strokeWidth="2.5" markerEnd="url(#qr_a2)"/>
        <text x={ox+a2[0]*sc+5} y={oy-a2[1]*sc+4} fontSize="13" fill="#c0392b" fontWeight="700">a₂</text>

        {/* projection of a2 onto q1 */}
        <line x1={ox} y1={oy} x2={ox+proj[0]*sc-2} y2={oy-proj[1]*sc}
          stroke="#e07b00" strokeWidth="2} " strokeDasharray="5,3" markerEnd="url(#qr_proj)"/>
        <text x={ox+proj[0]*sc+5} y={oy-proj[1]*sc+14} fontSize="11.5" fill="#e07b00">proj</text>

        {/* perpendicular component (a2 - proj) */}
        <line x1={ox+proj[0]*sc} y1={oy-proj[1]*sc}
              x2={ox+proj[0]*sc+perp[0]*sc-1} y2={oy-proj[1]*sc-perp[1]*sc}
          stroke="#9b59b6" strokeWidth="2" strokeDasharray="5,3" markerEnd="url(#qr_perp)"/>
        <text x={ox+proj[0]*sc+perp[0]*sc+5} y={oy-proj[1]*sc-perp[1]*sc+4}
          fontSize="11.5" fill="#9b59b6">a₂−proj</text>

        {/* right angle marker at proj tip */}
        {(() => {
          const px = ox+proj[0]*sc, py = oy-proj[1]*sc;
          const sz = 10;
          const ux = q1[0], uy = -q1[1];
          const vx = q2[0], vy = -q2[1];
          return (
            <path d={`M${px+ux*sz},${py+uy*sz} L${px+ux*sz+vx*sz},${py+uy*sz+vy*sz} L${px+vx*sz},${py+vy*sz}`}
              fill="none" stroke="#888" strokeWidth="1.2"/>
          );
        })()}

        {/* q1, q2 — final orthonormal result */}
        <line x1={ox} y1={oy} x2={ox+q1[0]*sc-2} y2={oy-q1[1]*sc}
          stroke="#27ae60" strokeWidth="3" markerEnd="url(#qr_q1)"/>
        <text x={ox+q1[0]*sc+5} y={oy-q1[1]*sc-6} fontSize="13" fill="#27ae60" fontWeight="700">q₁</text>

        <line x1={ox} y1={oy} x2={ox+q2[0]*sc} y2={oy-q2[1]*sc}
          stroke="#9b59b6" strokeWidth="3" markerEnd="url(#qr_q2)"/>
        <text x={ox+q2[0]*sc-22} y={oy-q2[1]*sc-6} fontSize="13" fill="#9b59b6" fontWeight="700">q₂</text>

        {/* Right angle between q1 and q2 */}
        {(() => {
          const sz = 12;
          const ux = q1[0], uy = -q1[1];
          const vx = q2[0], vy = -q2[1];
          return (
            <path d={`M${ox+ux*sz},${oy+uy*sz} L${ox+ux*sz+vx*sz},${oy+uy*sz+vy*sz} L${ox+vx*sz},${oy+vy*sz}`}
              fill="none" stroke="#27ae60" strokeWidth="1.5"/>
          );
        })()}

        <rect x={18} y={296} width={396} height={36} rx={4} fill="#f0fff4" stroke="#27ae60" strokeWidth="1"/>
        <text x={216} y={311} textAnchor="middle" fontSize="11.5" fill="#27ae60" fontWeight="700">
          q₁ · q₂ = 0  (orthogonal)    |q₁| = |q₂| = 1  (unit length)
        </text>
        <text x={216} y={326} textAnchor="middle" fontSize="11" fill="#555">
          QᵀQ = I  — columns are orthonormal by construction
        </text>

        {/* ── RIGHT: A=QR equation + least squares ── */}
        <rect x={446} y={8} width={424} height={332} rx={6} fill="#fff" stroke="#2E75B6" strokeWidth="1.6"/>
        <text x={658} y={26} textAnchor="middle" fontSize="13" fill="#1F3864" fontWeight="700">
          A = QR — Matrix Form and Least-Squares Connection
        </text>

        {/* A = QR display */}
        <text x={530} y={56} textAnchor="middle" fontSize="12.5" fill="#1F3864" fontWeight="700">A = Q · R</text>
        <Mat data={[['a₁','a₂'],['|','|']]} x0={454} y0={66} cW={44} cH={38} color="#1F3864" fSize={12}/>
        <text x={558} y={92} fontSize="22" fill="#aaa">=</text>
        <Mat data={[['q₁','q₂'],['|','|']]} x0={572} y0={66} cW={44} cH={38} color="#27ae60" fSize={12}/>
        <text x={672} y={92} fontSize="22" fill="#aaa">·</text>
        <Mat data={[['r₁₁','r₁₂'],['0','r₂₂']]} x0={688} y0={66} cW={52} cH={38} color="#2E75B6" fSize={12}
          colors={[['#d0e8ff','#d0e8ff'],[null,'#d0e8ff']]}/>

        {/* R structure note */}
        <text x={810} y={74} fontSize="11" fill="#2E75B6">R is upper</text>
        <text x={810} y={88} fontSize="11" fill="#2E75B6">triangular</text>
        <text x={810} y={104} fontSize="11" fill="#888">rᵢⱼ = aⱼ·qᵢ</text>

        <line x1={454} y1={134} x2={862} y2={134} stroke="#eee" strokeWidth="1"/>

        {/* Least squares via QR */}
        <text x={658} y={153} textAnchor="middle" fontSize="12.5" fill="#1F3864" fontWeight="700">
          QR Least Squares — Solving min‖Ax−b‖²
        </text>
        <text x={454} y={172} fontSize="11.5" fill="#555">Ax = b  →  QRx = b  →  Rx = Qᵀb</text>
        <rect x={454} y={182} width={398} height={26} rx={3} fill="#EBF3FB"/>
        <text x={653} y={200} textAnchor="middle" fontSize="13" fill="#2E75B6"
          fontFamily="'Courier New',monospace" fontWeight="700">x̂ = R⁻¹Qᵀb</text>

        {/* Comparison with normal equations */}
        <line x1={454} y1={218} x2={862} y2={218} stroke="#eee" strokeWidth="1"/>
        <text x={658} y={237} textAnchor="middle" fontSize="12" fill="#1F3864" fontWeight="700">
          QR vs Normal Equations for Least Squares
        </text>

        {[
          ['Normal equations', '(AᵀA)x̂=Aᵀb', 'Condition number κ(AᵀA) = κ(A)²', '#c0392b', 'Can lose accuracy'],
          ['QR decomposition', 'Rx̂=Qᵀb',      'Condition number κ(A) — much better', '#27ae60', 'Numerically preferred'],
        ].map(([method, eq, cond, color, verdict], i) => (
          <g key={i}>
            <rect x={454} y={248+i*38} width={398} height={32} rx={3}
              fill={i===0?'#fff0f0':'#f0fff4'} stroke={color} strokeWidth="1"/>
            <text x={462} y={261+i*38} fontSize="12" fill={color} fontWeight="700">{method}:</text>
            <text x={462} y={275+i*38} fontSize="11" fill="#1F3864" fontFamily="'Courier New',monospace">{eq} — {cond}</text>
          </g>
        ))}

        <rect x={454} y={330} width={398} height={0} rx={3}/>
        <text x={658} y={326} textAnchor="middle" fontSize="11" fill="#888">
          numpy.linalg.lstsq() and MATLAB's \ use QR internally, not normal equations
        </text>
      </svg>
      <div style={S.dCaption}>
        Gram-Schmidt takes the columns of A and builds an orthonormal set Q one at a time: normalise the first column to get q₁, subtract the q₁ component from the second column, then normalise the remainder to get q₂, and so on. The upper-triangular R records the original projections. For least squares, QR is numerically preferred over the normal equations because it avoids squaring the condition number.
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────
// DIAGRAM 4 — SVD: geometric interpretation + structure
// A = UΣVᵀ with the four spaces, singular values, and truncated SVD
// ─────────────────────────────────────────────────────────────────
function DiagramSVD() {
  const W = 880, H = 380;
  const sc = 46;

  return (
    <div style={S.dWrap}>
      <div style={S.dTitle}>Singular Value Decomposition — A = UΣVᵀ, the Most Informative Factorisation</div>
      <svg viewBox={`0 0 ${W} ${H}`} style={{ width:'100%', display:'block' }}>
        <defs>
          <Arr id="svd_v1"  color="#2E75B6"/>
          <Arr id="svd_v2"  color="#2E75B6"/>
          <Arr id="svd_u1"  color="#c0392b"/>
          <Arr id="svd_u2"  color="#c0392b"/>
          <Arr id="svd_s1"  color="#27ae60"/>
          <Arr id="svd_s2"  color="#9b59b6"/>
        </defs>

        {/* ── TOP: A = U Σ Vᵀ matrix display ── */}
        <text x={50}  y={24} textAnchor="middle" fontSize="14" fill="#1F3864" fontWeight="700">A  (m×n)</text>
        <Mat data={[['a','b'],['c','d'],['e','f']]} x0={10} y0={34} cW={44} cH={36} color="#1F3864" fSize={12}/>
        <text x={124} y={70} fontSize="24" fill="#aaa">=</text>

        {/* U */}
        <text x={208} y={14} textAnchor="middle" fontSize="13" fill="#c0392b" fontWeight="700">U  (m×m, orthogonal)</text>
        <text x={208} y={27} textAnchor="middle" fontSize="10.5" fill="#c0392b">left singular vectors</text>
        <Mat data={[['u₁','u₂','u₃'],['|','|','|'],['|','|','|']]} x0={148} y0={34} cW={44} cH={36} color="#c0392b" fSize={11}
          colors={[[null,null,null],['#ffd0d0','#ffd0d0','#ffe0e0'],['#ffd0d0','#ffd0d0','#ffe0e0']]}/>
        <text x={286} y={70} fontSize="24" fill="#aaa">·</text>

        {/* Σ */}
        <text x={370} y={14} textAnchor="middle" fontSize="13" fill="#27ae60" fontWeight="700">Σ  (m×n, diagonal)</text>
        <text x={370} y={27} textAnchor="middle" fontSize="10.5" fill="#27ae60">singular values σ₁≥σ₂≥…≥0</text>
        <Mat data={[['σ₁','0','0'],['0','σ₂','0'],['0','0','0']]} x0={306} y0={34} cW={48} cH={36} color="#27ae60" fSize={12}
          colors={[['#d4f0d4',null,null],[null,'#d4f0d4',null],[null,null,null]]}/>
        <text x={448} y={70} fontSize="24" fill="#aaa">·</text>

        {/* Vᵀ */}
        <text x={556} y={14} textAnchor="middle" fontSize="13" fill="#2E75B6" fontWeight="700">Vᵀ  (n×n, orthogonal)</text>
        <text x={556} y={27} textAnchor="middle" fontSize="10.5" fill="#2E75B6">right singular vectors (rows)</text>
        <Mat data={[['—v₁—'],['—v₂—']]} x0={466} y0={34} cW={120} cH={36} color="#2E75B6" fSize={11}
          colors={[['#d0e8ff'],['#d0ecff']]}/>

        {/* Sum of rank-1 matrices interpretation */}
        <rect x={632} y={8} width={240} height={110} rx={5} fill="#fff" stroke="#e07b00" strokeWidth="1.4"/>
        <text x={752} y={26} textAnchor="middle" fontSize="12" fill="#e07b00" fontWeight="700">Rank-1 outer product form</text>
        <text x={642} y={44} fontSize="12" fill="#1F3864" fontFamily="'Courier New',monospace">A = σ₁u₁v₁ᵀ + σ₂u₂v₂ᵀ + …</text>
        <line x1={642} y1={55} x2={862} y2={55} stroke="#eee" strokeWidth="1"/>
        <text x={752} y={72} textAnchor="middle" fontSize="11.5" fill="#555">Each term is one rank-1 layer</text>
        <text x={752} y={88} textAnchor="middle" fontSize="11.5" fill="#555">σ₁ is the most important layer</text>
        <text x={752} y={104} textAnchor="middle" fontSize="11" fill="#888">Keep top k terms → rank-k approx</text>

        {/* Divider */}
        <line x1={10} y1={128} x2={870} y2={128} stroke="#e0e8f0" strokeWidth="1.5"/>

        {/* ── MIDDLE LEFT: geometric view — stretch and rotate ── */}
        <text x={220} y={148} textAnchor="middle" fontSize="13" fill="#1F3864" fontWeight="700">
          Geometric Interpretation — Three Steps
        </text>

        {/* Step panels */}
        {[
          { label:'① Vᵀ rotates input', color:'#2E75B6', bg:'#d0e8ff', note:'Input unit circle\naligned to v directions', x:10 },
          { label:'② Σ stretches axes',  color:'#27ae60', bg:'#d4f0d4', note:'Circle → ellipse\nσ₁, σ₂ = semi-axes', x:222 },
          { label:'③ U rotates output', color:'#c0392b', bg:'#ffd0d0', note:'Ellipse reoriented\ninto output space', x:434 },
        ].map((st, i) => {
          const cx = st.x + 100, cy = 230;
          return (
            <g key={i}>
              <rect x={st.x} y={155} width={200} height={150} rx={5}
                fill="#fff" stroke={st.color} strokeWidth="1.6"/>
              <rect x={st.x} y={155} width={200} height={22} rx={5} fill={st.bg}/>
              <rect x={st.x} y={167} width={200} height={10} fill={st.bg}/>
              <text x={cx} y={170} textAnchor="middle" fontSize="11.5" fill={st.color} fontWeight="700">{st.label}</text>

              {/* Mini geometric illustration */}
              {i === 0 && (
                <g>
                  {/* Unit circle */}
                  <circle cx={cx} cy={cy} r={34} fill={st.bg} opacity="0.3" stroke={st.color} strokeWidth="1.5"/>
                  {/* v1 direction */}
                  <line x1={cx} y1={cy} x2={cx+32} y2={cy-14}
                    stroke={st.color} strokeWidth="2" markerEnd="url(#svd_v1)"/>
                  <text x={cx+34} y={cy-10} fontSize="11" fill={st.color} fontWeight="700">v₁</text>
                  <line x1={cx} y1={cy} x2={cx-14} y2={cy-32}
                    stroke={st.color} strokeWidth="2" markerEnd="url(#svd_v2)"/>
                  <text x={cx-28} y={cy-32} fontSize="11" fill={st.color} fontWeight="700">v₂</text>
                </g>
              )}
              {i === 1 && (
                <g>
                  {/* Ellipse — σ1=2.2, σ2=1 */}
                  <ellipse cx={cx} cy={cy} rx={46} ry={22} fill={st.bg} opacity="0.35" stroke={st.color} strokeWidth="1.5"/>
                  <line x1={cx} y1={cy} x2={cx+44} y2={cy}
                    stroke={st.color} strokeWidth="2.5" markerEnd="url(#svd_s1)"/>
                  <text x={cx+46} y={cy-4} fontSize="11" fill={st.color} fontWeight="700">σ₁</text>
                  <line x1={cx} y1={cy} x2={cx} y2={cy-20}
                    stroke="#9b59b6" strokeWidth="2.5" markerEnd="url(#svd_s2)"/>
                  <text x={cx+4} y={cy-22} fontSize="11" fill="#9b59b6" fontWeight="700">σ₂</text>
                </g>
              )}
              {i === 2 && (
                <g>
                  {/* Rotated ellipse */}
                  <ellipse cx={cx} cy={cy} rx={46} ry={22} fill={st.bg} opacity="0.35" stroke={st.color} strokeWidth="1.5"
                    transform={`rotate(-35,${cx},${cy})`}/>
                  <line x1={cx} y1={cy}
                    x2={cx + 44*Math.cos(-0.61)} y2={cy + 44*Math.sin(-0.61)}
                    stroke={st.color} strokeWidth="2.5" markerEnd="url(#svd_u1)"/>
                  <text x={cx+46*Math.cos(-0.61)+3} y={cy+46*Math.sin(-0.61)+4}
                    fontSize="11" fill={st.color} fontWeight="700">u₁</text>
                  <line x1={cx} y1={cy}
                    x2={cx + 22*Math.cos(-0.61+Math.PI/2)} y2={cy + 22*Math.sin(-0.61+Math.PI/2)}
                    stroke={st.color} strokeWidth="2" markerEnd="url(#svd_u2)"/>
                  <text x={cx+24*Math.cos(-0.61+Math.PI/2)+4} y={cy+24*Math.sin(-0.61+Math.PI/2)+4}
                    fontSize="11" fill={st.color} fontWeight="700">u₂</text>
                </g>
              )}

              {st.note.split('\n').map((line, li) => (
                <text key={li} x={cx} y={283+li*15} textAnchor="middle" fontSize="10.5" fill="#555">{line}</text>
              ))}

              {i < 2 && <text x={st.x+208} y={228} fontSize="20" fill="#aaa">→</text>}
            </g>
          );
        })}

        {/* ── RIGHT: Four subspaces from SVD ── */}
        <rect x={646} y={155} width={226} height={150} rx={5} fill="#fff" stroke="#9b59b6" strokeWidth="1.6"/>
        <text x={759} y={172} textAnchor="middle" fontSize="12" fill="#9b59b6" fontWeight="700">SVD Reveals the Four Subspaces</text>
        {[
          ['Col space C(A)',   '#2E75B6', 'first r cols of U  (r = rank)'],
          ['Left null N(Aᵀ)', '#c0392b', 'last m−r cols of U'],
          ['Row space C(Aᵀ)', '#27ae60', 'first r rows of Vᵀ'],
          ['Null space N(A)', '#9b59b6', 'last n−r rows of Vᵀ'],
        ].map(([label, color, note], i) => (
          <g key={i}>
            <rect x={654} y={182+i*28} width={210} height={24} rx={3}
              fill={i%2===0?'#f8fbff':'#fff'} stroke="#eee" strokeWidth="1"/>
            <text x={660} y={198+i*28} fontSize="11" fill={color} fontWeight="700">{label}:</text>
            <text x={660} y={210+i*28} fontSize="10.5" fill="#555" fontFamily="'Courier New',monospace">{note}</text>
          </g>
        ))}
        <text x={759} y={302} textAnchor="middle" fontSize="10.5" fill="#888">{"σᵢ > 0 for i ≤ rank(A)"}</text>

        {/* ── BOTTOM: Truncated SVD ── */}
        <line x1={10} y1={318} x2={870} y2={318} stroke="#e0e8f0" strokeWidth="1.5"/>
        <text x={440} y={337} textAnchor="middle" fontSize="13" fill="#1F3864" fontWeight="700">
          Truncated SVD — Best Rank-k Approximation (Eckart-Young Theorem)
        </text>
        <text x={440} y={355} textAnchor="middle" fontSize="12" fill="#2E75B6" fontFamily="'Courier New',monospace" fontWeight="700">
          Aₖ = σ₁u₁v₁ᵀ + σ₂u₂v₂ᵀ + … + σₖuₖvₖᵀ
        </text>
        <text x={440} y={372} textAnchor="middle" fontSize="11.5" fill="#555">
          Aₖ is the closest rank-k matrix to A in any unitarily invariant norm.  Error = σₖ₊₁  (next discarded singular value).
        </text>
      </svg>
      <div style={S.dCaption}>
        SVD factorises any m×n matrix — no symmetry or square requirement. The singular values σᵢ on the diagonal of Σ measure how much each "mode" contributes: large σ means important direction, near-zero σ means near-redundant. Truncated SVD keeps only the top k modes, giving the best possible rank-k approximation — the engine behind image compression, PCA, and model reduction.
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────
// DIAGRAM 5 — Decomposition Chooser: decision flowchart
// ─────────────────────────────────────────────────────────────────
function DiagramChooser() {
  const W = 880, H = 330;

  const decomps = [
    {
      name: 'LU  (PA=LU)',
      color: '#2E75B6', bg: '#d0e8ff',
      when: 'Square matrix, general\n(no special structure)',
      use:  'Solve Ax=b (single or many b)\nDeterminant via product of U diagonal',
      code: 'scipy.linalg.lu(A)\nnumpy.linalg.solve(A,b)',
      cost: 'O(n³) factor,  O(n²) solve',
      x: 10,
    },
    {
      name: 'Cholesky  (A=LLᵀ)',
      color: '#9b59b6', bg: '#f0e0ff',
      when: 'Symmetric positive definite\n(stiffness, covariance, …)',
      use:  'Solve Ax=b  (2× faster than LU)\nTest if matrix is truly PD',
      code: 'numpy.linalg.cholesky(A)\nscipy.linalg.cho_solve(…)',
      cost: 'O(n³/3) factor — half LU flops',
      x: 228,
    },
    {
      name: 'QR',
      color: '#27ae60', bg: '#d4f0d4',
      when: 'Overdetermined system (m>n)\nLeast squares fitting',
      use:  'Numerically stable min‖Ax−b‖²\nOrthonormal basis for C(A)',
      code: 'numpy.linalg.qr(A)\nscipy.linalg.lstsq(A,b)',
      cost: 'O(mn²) for m×n, m≥n',
      x: 446,
    },
    {
      name: 'Eigendecomposition  (A=PDP⁻¹)',
      color: '#e07b00', bg: '#fff3cc',
      when: 'Square matrix, need eigenvalues\nVibration, stability, PCA',
      use:  'Natural frequencies, mode shapes\nMatrix powers Aⁿ=PDⁿP⁻¹',
      code: 'numpy.linalg.eig(A)\nnumpy.linalg.eigh(A) for sym.',
      cost: 'O(n³)',
      x: 10,
      y2: true,
    },
    {
      name: 'SVD  (A=UΣVᵀ)',
      color: '#c0392b', bg: '#ffd0d0',
      when: 'Any matrix (m×n), rank/subspaces\nCompression, pseudo-inverse',
      use:  'Rank, four subspaces, cond. number\nBest rank-k approx, least squares',
      code: 'numpy.linalg.svd(A)\nnumpy.linalg.pinv(A)  (pseudo-inv)',
      cost: 'O(mn·min(m,n)) — most expensive',
      x: 228,
      y2: true,
    },
    {
      name: 'Sparse / Iterative',
      color: '#555', bg: '#eeeeee',
      when: 'Very large n (FEA: n>100 000)\nMost entries are zero',
      use:  'FEA global stiffness solve\nIterative: CG, GMRES, etc.',
      code: 'scipy.sparse.linalg.spsolve\nANSYS / Abaqus internal solvers',
      cost: 'O(n·nnz) per iteration',
      x: 446,
      y2: true,
    },
  ];

  const ROW2_Y = 175;

  return (
    <div style={S.dWrap}>
      <div style={S.dTitle}>Decomposition Chooser — Which Factorisation to Use and When</div>
      <svg viewBox={`0 0 ${W} ${H}`} style={{ width:'100%', display:'block' }}>
        {decomps.map((d, i) => {
          const pw = 206, ph = 155;
          const py = d.y2 ? ROW2_Y : 8;
          return (
            <g key={i}>
              <rect x={d.x} y={py} width={pw} height={ph} rx={6}
                fill="#fff" stroke={d.color} strokeWidth="1.8"/>
              <rect x={d.x} y={py} width={pw} height={26} rx={6} fill={d.bg}/>
              <rect x={d.x} y={py+16} width={pw} height={10} fill={d.bg}/>
              <text x={d.x+pw/2} y={py+18} textAnchor="middle" fontSize="11.5"
                fill={d.color} fontWeight="700">{d.name}</text>

              <text x={d.x+8} y={py+40} fontSize="10.5" fill="#1F3864" fontWeight="700">When:</text>
              {d.when.split('\n').map((line, li) => (
                <text key={li} x={d.x+8} y={py+54+li*14} fontSize="10.5" fill="#555">{line}</text>
              ))}

              <text x={d.x+8} y={py+86} fontSize="10.5" fill="#1F3864" fontWeight="700">Use for:</text>
              {d.use.split('\n').map((line, li) => (
                <text key={li} x={d.x+8} y={py+99+li*14} fontSize="10.5" fill="#555">{line}</text>
              ))}

              <rect x={d.x+6} y={py+128} width={pw-12} height={20} rx={3} fill={d.bg} opacity="0.4"/>
              <text x={d.x+10} y={py+141} fontSize="10" fill={d.color}
                fontFamily="'Courier New',monospace">{d.cost}</text>
            </g>
          );
        })}

        {/* Code callout strip at bottom */}
        <rect x={10} y={300} width={860} height={24} rx={4} fill="#1F3864" opacity="0.08"/>
        <text x={440} y={316} textAnchor="middle" fontSize="11.5" fill="#1F3864" fontWeight="700">
          Rule of thumb: symmetric PD → Cholesky.  General square → LU.  Overdetermined → QR.  Need eigenvalues → eig.  Need everything → SVD.
        </text>
      </svg>
      <div style={S.dCaption}>
        Choosing the right decomposition matters enormously for speed and numerical health. Cholesky for stiffness matrices, QR for fitting problems, SVD when you need the full picture. Sparse iterative solvers take over once the matrix is too large to store densely — the standard in production FEA codes.
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────
// MAIN COMPONENT
// ─────────────────────────────────────────────────────────────────
export default function MatrixDecompositions() {
  return (
    <div style={S.wrapper}>
      <h1 style={S.h1}>Matrix Decompositions</h1>

      <p style={S.p}>
        The Matrices page shows that solving Ax=b requires an inverse, and the Matrix Operations page introduced LU as the engine behind real solvers. This page goes deeper on all four major decompositions — LU, Cholesky, QR, and SVD — explaining the structure of each, when to use it, and what it reveals about the matrix beyond just a solution. Together these four factorisations underlie virtually every numerical tool in engineering: FEA solvers, least-squares fitting, modal analysis, image compression, and model reduction.
      </p>

      <InfoBox
        title="Why Decompose? — Three Reasons to Factor a Matrix Rather Than Invert It"
        items={[
          "Speed: factoring once (O(n³)) then solving repeatedly (O(n²) each) beats re-inverting for every new right-hand side",
          "Stability: triangular solves are numerically well-conditioned; forming A⁻¹ explicitly amplifies rounding errors",
          "Insight: each factorisation exposes different structure — rank, subspaces, condition number, spectral content",
        ]}
      />

      {/* ── LU ── */}
      <h2 style={S.h2}>LU Decomposition — PA = LU</h2>
      <DiagramLU />
      <Table2 rows={[
        ["What it produces",       "L: lower triangular, unit diagonal (Doolittle) or pivot diagonal (Crout)\nU: upper triangular with pivots on diagonal\nP: permutation matrix recording row swaps"],
        ["Partial pivoting",       "Before each elimination step, swap rows to place the largest element in the pivot position\nPrevents small pivots from amplifying rounding error — PA=LU, not A=LU"],
        ["Determinant from LU",    "det(A) = (−1)ˢ · U[1,1]·U[2,2]·…·U[n,n]\nwhere s = number of row swaps in P — free once factored"],
        ["Multiple right-hand sides","Factor A once: O(n³).  Each new b: two triangular sweeps O(n²)\nFEA load-case sweeps, sensitivity analyses all exploit this"],
        ["When LU fails",          "Exact zero pivot with no valid swap: matrix is singular\nNear-zero pivot with partial pivoting: matrix is ill-conditioned — check condition number"],
        ["In code",                "scipy.linalg.lu(A) → P, L, U\nnumpy.linalg.solve(A, b) uses LU internally with pivoting"],
      ]}/>

      {/* ── Cholesky ── */}
      <h2 style={S.h2}>Cholesky Decomposition — A = LLᵀ</h2>
      <DiagramCholesky />
      <Table2 rows={[
        ["Requirement",            "A must be symmetric (A=Aᵀ) AND positive definite (all eigenvalues > 0)\nEquivalent test: Cholesky completes without a negative square-root argument"],
        ["Structure",              "A = LLᵀ where L is lower triangular with positive diagonal entries\nLᵀ is the upper triangle — mirror of L, no extra storage needed"],
        ["Why half the flops",     "Exploits symmetry — only the lower triangle of A is read\n~n³/3 flops vs ~2n³/3 for LU — exactly half the work"],
        ["Positive definiteness check","If the factorisation reaches a negative diagonal entry → A is not PD\nUsed as a fast numerical test in optimisation and FEA pre-checks"],
        ["LDLᵀ variant",          "A = LDLᵀ where D is diagonal and L has unit diagonal\nAvoids square roots — faster on hardware without fast sqrt, numerically equivalent"],
        ["In code",                "numpy.linalg.cholesky(A) → L\nscipy.linalg.cho_factor + cho_solve for repeated solves"],
      ]}/>

      {/* ── QR ── */}
      <h2 style={S.h2}>QR Decomposition — A = QR</h2>
      <DiagramQR />
      <Table2 rows={[
        ["What it produces",       "Q: orthogonal (QᵀQ=I), same column space as A\nR: upper triangular, diagonal entries = lengths of successive Gram-Schmidt vectors"],
        ["Gram-Schmidt (classical)","Subtract the projection onto all previous q vectors, then normalise\nSimple to understand but numerically unstable for nearly-dependent columns"],
        ["Modified Gram-Schmidt",  "Subtract projections sequentially (not all at once) — same result, much better numerics\nDefault in most implementations"],
        ["Householder reflections","Reflect each column to align with an axis using a rank-1 update\nO(2mn²−2n³/3) flops, numerically superior — used in LAPACK/numpy"],
        ["Least squares via QR",   "min‖Ax−b‖² → QRx=b → Rx=Qᵀb → back-substitution\nCondition number of R equals κ(A), not κ(A)² as with normal equations"],
        ["In code",                "numpy.linalg.qr(A) → Q, R\nnumpy.linalg.lstsq(A, b) uses QR-based algorithm internally"],
      ]}/>

      {/* ── SVD ── */}
      <h2 style={S.h2}>Singular Value Decomposition — A = UΣVᵀ</h2>
      <DiagramSVD />
      <Table2 rows={[
        ["What it produces",       "U (m×m): left singular vectors — orthonormal basis for output space\nΣ (m×n): singular values σ₁≥σ₂≥…≥0 on diagonal\nV (n×n): right singular vectors — orthonormal basis for input space"],
        ["Singular values vs eigenvalues","σᵢ = √(eigenvalues of AᵀA) — always real and non-negative\nEigenvalues can be complex; singular values never are"],
        ["Rank and condition number","rank(A) = number of non-zero singular values\nκ(A) = σ₁/σₙ — condition number; large κ means ill-conditioned"],
        ["Pseudo-inverse A⁺",      "A⁺ = VΣ⁺Uᵀ where Σ⁺ inverts each non-zero σᵢ, leaves zeros alone\nGives the minimum-norm least-squares solution for any Ax=b"],
        ["Truncated SVD",          "Aₖ = Σᵢ₌₁ᵏ σᵢuᵢvᵢᵀ — best rank-k approximation (Eckart-Young theorem)\nUsed in: image compression, PCA, reduced-order models, noise filtering"],
        ["In code",                "numpy.linalg.svd(A) → U, s, Vt\nnumpy.linalg.matrix_rank(A), numpy.linalg.pinv(A)"],
      ]}/>

      {/* ── Chooser ── */}
      <h2 style={S.h2}>Choosing the Right Decomposition</h2>
      <DiagramChooser />
      <Table2 rows={[
        ["SVD ⊇ all others",        "SVD is the most general: it gives rank, four subspaces, condition number, pseudo-inverse, and the best low-rank approximation\nUse it when you need the full picture; use specialised decompositions for speed"],
        ["Eigendecomp vs SVD",      "Eigendecomposition: square matrices, may need complex arithmetic, P may be ill-conditioned\nSVD: any shape, always real and stable, U and V are orthogonal"],
        ["Cholesky vs LU",          "Always prefer Cholesky for symmetric PD — it's twice as fast and unconditionally stable\nLU is the fallback for general square matrices"],
        ["QR vs normal equations",  "Normal equations (AᵀA)x̂=Aᵀb square the condition number — dangerous for ill-conditioned A\nQR avoids this: same accuracy with a single factorisation"],
        ["Sparse systems",          "Dense methods cost O(n³) — infeasible for n > ~10 000\nExploit sparsity: reordering (AMD, nested dissection) + sparse Cholesky/LU\nIterative methods (CG for PD, GMRES for general) cost O(n·nnz) per iteration"],
        ["Practical hierarchy",     "1. Is A symmetric PD? → Cholesky\n2. Is A square, general? → LU with pivoting\n3. Is system overdetermined (m>n)? → QR\n4. Need rank/subspaces/compression? → SVD"],
      ]}/>

      <InfoBox
        title="Key Takeaway — One Decomposition Map"
        items={[
          "LU: the workhorse for square systems — factor once, solve many. Always use pivoting (PA=LU).",
          "Cholesky: LU's faster, simpler sibling for symmetric positive definite matrices — use it whenever you can.",
          "QR: the numerically correct way to do least squares — avoids squaring the condition number.",
          "SVD: the most revealing factorisation — exposes rank, four subspaces, condition number, and gives the best low-rank approximation. Slower but unmatched in diagnostic power.",
          "In practice: numpy.linalg.solve → LU. numpy.linalg.lstsq → QR. numpy.linalg.svd → full SVD. numpy.linalg.cholesky → Cholesky.",
        ]}
      />
    </div>
  );
}